import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ChevronDown,
  ChevronUp,
  CreditCard,
  FileText,
  ArrowRight,
  Calendar,
  Clock,
  AlertTriangle,
  Sparkles,
  XCircle,
  RefreshCw,
  BarChart3,
  Check,
  Infinity,
  Pause,
  Play,
  Rocket,
  Building,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { BrandedLoading, BrandedSpinner } from '@/components/common/BrandedLoading';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import { billingApi } from '@/services/billingApi';
import { subscriptionPlanApi } from '@/services/subscriptionApi';
import type { SubscriptionPlan } from '@/types/subscription';
import type { Subscription, SubscriptionStatus } from '@/types/billing';
import { PlanComparisonTable } from '@/components/billing/PlanComparisonTable';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_CONFIG: Record<
  SubscriptionStatus,
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; className?: string }
> = {
  ACTIVE: { label: 'Active', variant: 'default', className: 'bg-emerald-600 hover:bg-emerald-600' },
  TRIAL: { label: 'Trial', variant: 'default', className: 'bg-blue-600 hover:bg-blue-600' },
  PENDING: { label: 'Pending', variant: 'outline' },
  PAST_DUE: { label: 'Past Due', variant: 'destructive' },
  CANCELLED: { label: 'Cancelled', variant: 'secondary' },
  SUSPENDED: { label: 'Suspended', variant: 'destructive' },
  EXPIRED: { label: 'Expired', variant: 'secondary' },
  INACTIVE: { label: 'Inactive', variant: 'secondary' },
  PAUSED: { label: 'Paused', variant: 'outline', className: 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-100' },
};

// Map iconName from seed data to lucide icon components
const ICON_MAP: Record<string, React.ElementType> = {
  sparkles: Sparkles,
  rocket: Rocket,
  building: Building,
};

/** Render a limit value: -1 or null/undefined = "Unlimited" */
const formatLimitValue = (value: number | undefined | null): React.ReactNode => {
  if (value === undefined || value === null || value === -1) {
    return (
      <span className="inline-flex items-center gap-1">
        <Infinity className="h-3 w-3" />
        Unlimited
      </span>
    );
  }
  return value.toLocaleString();
};

const formatCurrency = (amount: number, currency = 'USD') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);

const formatCurrencySafe = (amount: unknown, currency = 'USD'): string => {
  const num = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(num)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: num % 1 === 0 ? 0 : 2,
  }).format(num);
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

const getDaysUntil = (iso: string): number => {
  const diff = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

const billingCycleLabel = (cycle: string) => {
  switch (cycle) {
    case 'MONTHLY':
      return 'Monthly';
    case 'QUARTERLY':
      return 'Quarterly';
    case 'YEARLY':
      return 'Yearly';
    default:
      return cycle;
  }
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const BillingDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [currentPlan, setCurrentPlan] = useState<SubscriptionPlan | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const [isPauseResumeLoading, setIsPauseResumeLoading] = useState(false);
  const [showComparePlans, setShowComparePlans] = useState(false);

  const priceForCycleFromPlan = (plan: SubscriptionPlan | null, cycle: Subscription['billingCycle']): number | null => {
    if (!plan) return null;
    if (cycle === 'YEARLY') return plan.yearlyPrice;
    if (cycle === 'QUARTERLY') return plan.monthlyPrice * 3;
    return plan.monthlyPrice;
  };

  const getPlanFeaturesPreview = (plan: SubscriptionPlan | null): string[] => {
    const features = plan?.features;
    if (!features) return [];
    if (Array.isArray(features)) return (features as unknown[]).map(String).slice(0, 4);
    if (typeof features === 'object') {
      return Object.entries(features as Record<string, unknown>)
        .map(([k, v]) => (typeof v === 'boolean' ? (v ? k : '') : `${k}: ${String(v)}`))
        .filter(Boolean)
        .slice(0, 4);
    }
    return [];
  };

  const getPlanLimitsPreview = (plan: SubscriptionPlan | null): Array<{ key: string; value: string }> => {
    const limits = plan?.limits;
    if (!limits || typeof limits !== 'object') return [];
    return Object.entries(limits as Record<string, number>)
      .map(([key, value]) => ({ key, value: String(value) }))
      .slice(0, 4);
  };

  const fetchSubscription = useCallback(async () => {
    if (!user?.id) return;
    try {
      setIsLoading(true);
      setError(null);
      const data = await billingApi.getActiveSubscription(user.id);
      setSubscription(data);
      setCurrentPlan(null);

      // Prefer plan pricing from subscription-plan API (prevents NaN if subscription.amount is missing/typed oddly)
      if (data?.subscriptionPlanId) {
        try {
          const plan = await subscriptionPlanApi.getById(data.subscriptionPlanId);
          setCurrentPlan(plan);
        } catch {
          // Non-critical: still render subscription card; price will fall back to subscription.amount
          setCurrentPlan(null);
        }
      }
    } catch (err: unknown) {
      const message = (err as Error)?.message ?? '';
      if (message.includes('Authentication required')) {
        navigate(`/login?next=${encodeURIComponent(location.pathname)}`, { replace: true });
        return;
      }
      // 404 = no subscription — not an error state
      if (message.includes('404') || message.toLowerCase().includes('not found')) {
        setSubscription(null);
      } else {
        setError(message || 'Failed to load subscription');
      }
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, navigate, location.pathname]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // ---- Loading state ----
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <Header />
        <main className="flex flex-col items-center justify-center min-h-[60vh] py-12 px-4">
          <BrandedLoading message="Loading billing information…" iconSize="md" />
        </main>
        <Footer />
      </div>
    );
  }

  // ---- Error state ----
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <Header />
        <main className="flex flex-col items-center justify-center min-h-[60vh] py-12 px-4">
          <div className="text-center space-y-4 max-w-md">
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto" />
            <p className="text-destructive">{error}</p>
            <Button onClick={fetchSubscription}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ---- No subscription state ----
  if (!subscription) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <Header />
        <main className="py-8 pt-20 sm:py-12 sm:pt-24">
          <div className="container max-w-4xl mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center space-y-6"
            >
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground">Billing</h1>
              <Card className="max-w-lg mx-auto">
                <CardContent className="pt-8 pb-8 text-center space-y-4">
                  <CreditCard className="h-16 w-16 mx-auto text-muted-foreground" />
                  <h2 className="text-xl font-semibold">No active subscription</h2>
                  <p className="text-muted-foreground max-w-sm mx-auto">
                    You don't have an active subscription yet. Choose a plan to get started with your career development journey.
                  </p>
                  <Button onClick={() => navigate('/billing/plans')}>
                    <Sparkles className="h-4 w-4 mr-2" />
                    View Plans
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ---- Main dashboard ----
  const statusCfg = STATUS_CONFIG[subscription.status] ?? STATUS_CONFIG.INACTIVE;
  const isTrial = subscription.status === 'TRIAL' || subscription.isTrialActive;
  const isCancelled = subscription.status === 'CANCELLED';
  const isPaused = subscription.status === ('PAUSED' as SubscriptionStatus);
  const isActive = subscription.status === 'ACTIVE';
  const isFreePlan = currentPlan?.planType === 'FREE' || currentPlan?.monthlyPrice === 0;
  const trialDaysLeft = subscription.trialEndDate ? getDaysUntil(subscription.trialEndDate) : 0;
  const nextBillingDays = subscription.nextBillingDate
    ? getDaysUntil(subscription.nextBillingDate)
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <Header />
      <main className="py-8 pt-20 sm:py-12 sm:pt-24">
        <div className="container max-w-4xl mx-auto px-4 sm:px-6 space-y-6">
          {/* Page heading */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
          >
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground">Billing</h1>
              <p className="text-muted-foreground mt-1">Manage your subscription and billing</p>
            </div>
          </motion.div>

          {/* Trial banner */}
          {isTrial && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="rounded-xl border border-blue-500/30 bg-blue-500/10 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
            >
              <div className="flex items-center gap-3">
                <Sparkles className="h-5 w-5 text-blue-600 shrink-0" />
                <div>
                  <p className="font-medium text-blue-900 dark:text-blue-100">
                    Free trial &mdash; {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} remaining
                  </p>
                  {subscription.trialEndDate && (
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      Trial ends on {formatDate(subscription.trialEndDate)}
                    </p>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => navigate('/billing/plans')}
                className="shrink-0"
              >
                Upgrade Now
              </Button>
            </motion.div>
          )}

          {/* Paused banner */}
          {isPaused && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
            >
              <div className="flex items-center gap-3">
                <Pause className="h-5 w-5 text-amber-600 shrink-0" />
                <div>
                  <p className="font-medium text-amber-900 dark:text-amber-100">
                    Subscription paused
                  </p>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    Your subscription is currently paused. Resume anytime to continue.
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                className="shrink-0"
                disabled={isPauseResumeLoading}
                onClick={async () => {
                  try {
                    setIsPauseResumeLoading(true);
                    await billingApi.resumeSubscription(subscription.id);
                    toast({ title: 'Subscription resumed', description: 'Your subscription is now active again.' });
                    fetchSubscription();
                  } catch (err: unknown) {
                    toast({
                      title: 'Resume failed',
                      description: (err as Error)?.message ?? 'Please try again.',
                      variant: 'destructive',
                    });
                  } finally {
                    setIsPauseResumeLoading(false);
                  }
                }}
              >
                {isPauseResumeLoading ? <BrandedSpinner className="mr-1" /> : <Play className="h-4 w-4 mr-1" />}
                Resume Now
              </Button>
            </motion.div>
          )}

          {/* Past due warning */}
          {subscription.status === 'PAST_DUE' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 flex items-center gap-3"
            >
              <AlertTriangle className="h-5 w-5 text-destructive shrink-0" />
              <p className="text-sm text-destructive">
                Your payment is past due. Please update your payment method to avoid service interruption.
              </p>
            </motion.div>
          )}

          {/* Current plan card */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <CardTitle className="text-xl">Current Plan</CardTitle>
                    <CardDescription className="mt-1">
                      {billingCycleLabel(subscription.billingCycle)} billing
                    </CardDescription>
                  </div>
                  <Badge variant={statusCfg.variant} className={statusCfg.className}>
                    {statusCfg.label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* Price */}
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Price</p>
                    <p className="text-2xl font-bold">
                      {(() => {
                        const planAmount = priceForCycleFromPlan(currentPlan, subscription.billingCycle);
                        const amountToShow = planAmount ?? subscription.amount;
                        return formatCurrencySafe(amountToShow, subscription.currency);
                      })()}
                      <span className="text-sm font-normal text-muted-foreground">
                        /{subscription.billingCycle === 'YEARLY' ? 'yr' : subscription.billingCycle === 'QUARTERLY' ? 'qtr' : 'mo'}
                      </span>
                    </p>
                  </div>

                  {/* Plan summary */}
                  <div className="space-y-1 sm:col-span-2">
                    {currentPlan ? (
                      <>
                        <p className="text-sm text-muted-foreground">Plan</p>
                        <div className="flex items-center gap-2">
                          {currentPlan.iconName && ICON_MAP[currentPlan.iconName] && (
                            (() => {
                              const Icon = ICON_MAP[currentPlan.iconName!];
                              return <Icon className="h-5 w-5 text-primary shrink-0" />;
                            })()
                          )}
                          <p className="font-medium leading-snug">
                            {currentPlan.displayName}
                          </p>
                          {currentPlan.badgeText && (
                            <Badge className="bg-gradient-to-r from-purple-600 to-primary text-white border-0 text-[10px]">
                              {currentPlan.badgeText}
                            </Badge>
                          )}
                        </div>

                        {/* Tagline */}
                        {currentPlan.tagline && (
                          <p className="text-sm text-primary/70 font-medium mt-1">
                            {currentPlan.tagline}
                          </p>
                        )}

                        <p className="text-sm text-muted-foreground mt-1">
                          {currentPlan.description}
                        </p>

                        {/* Feature Highlights (new seed data) */}
                        {currentPlan.featureHighlights && currentPlan.featureHighlights.length > 0 && (
                          <div className="pt-3">
                            <p className="text-sm text-muted-foreground mb-1">Includes</p>
                            <ul className="space-y-1.5">
                              {currentPlan.featureHighlights.map((f) => (
                                <li key={f} className="flex items-start gap-2 text-sm">
                                  <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                                  <span>{f}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Fallback to legacy features if no featureHighlights */}
                        {(!currentPlan.featureHighlights || currentPlan.featureHighlights.length === 0) &&
                          getPlanFeaturesPreview(currentPlan).length > 0 && (
                          <div className="pt-2">
                            <p className="text-sm text-muted-foreground">Includes</p>
                            <ul className="text-sm list-disc pl-5 space-y-1">
                              {getPlanFeaturesPreview(currentPlan).map((f) => (
                                <li key={f}>{f}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* New typed limits */}
                        {(currentPlan.dailyTokenLimit !== undefined || currentPlan.monthlyTokenLimit !== undefined || currentPlan.maxCoachSessionsPerDay !== undefined || currentPlan.maxLearningPaths !== undefined) && (
                          <div className="pt-3">
                            <p className="text-sm text-muted-foreground mb-1">Limits</p>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                              {currentPlan.dailyTokenLimit !== undefined && (
                                <div className="text-sm flex items-center justify-between gap-2">
                                  <span className="text-muted-foreground">Daily Tokens</span>
                                  <span className="font-medium">{formatLimitValue(currentPlan.dailyTokenLimit)}</span>
                                </div>
                              )}
                              {currentPlan.monthlyTokenLimit !== undefined && (
                                <div className="text-sm flex items-center justify-between gap-2">
                                  <span className="text-muted-foreground">Monthly Tokens</span>
                                  <span className="font-medium">{formatLimitValue(currentPlan.monthlyTokenLimit)}</span>
                                </div>
                              )}
                              {currentPlan.maxCoachSessionsPerDay !== undefined && (
                                <div className="text-sm flex items-center justify-between gap-2">
                                  <span className="text-muted-foreground">Coach Sessions/Day</span>
                                  <span className="font-medium">{formatLimitValue(currentPlan.maxCoachSessionsPerDay)}</span>
                                </div>
                              )}
                              {currentPlan.maxLearningPaths !== undefined && (
                                <div className="text-sm flex items-center justify-between gap-2">
                                  <span className="text-muted-foreground">Learning Paths</span>
                                  <span className="font-medium">{formatLimitValue(currentPlan.maxLearningPaths)}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Fallback to legacy limits if no typed limits */}
                        {currentPlan.dailyTokenLimit === undefined && currentPlan.monthlyTokenLimit === undefined &&
                          getPlanLimitsPreview(currentPlan).length > 0 && (
                          <div className="pt-2">
                            <p className="text-sm text-muted-foreground">Limits</p>
                            <div className="grid grid-cols-2 gap-x-3 gap-y-1 pt-1">
                              {getPlanLimitsPreview(currentPlan).map((l) => (
                                <div key={l.key} className="text-sm flex items-center justify-between gap-2">
                                  <span className="text-muted-foreground">{l.key}</span>
                                  <span className="font-medium">{Number(l.value) === -1 ? formatLimitValue(-1) : l.value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-muted-foreground">Plan</p>
                        <p className="font-medium leading-snug">
                          Current Plan
                        </p>
                      </>
                    )}
                  </div>

                  {/* Next billing */}
                  {subscription.nextBillingDate && !isCancelled && (
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Next Billing Date</p>
                      <p className="font-medium">{formatDate(subscription.nextBillingDate)}</p>
                      {nextBillingDays !== null && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {nextBillingDays === 0
                            ? 'Due today'
                            : `in ${nextBillingDays} day${nextBillingDays !== 1 ? 's' : ''}`}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Period */}
                  {subscription.currentPeriodStart && subscription.currentPeriodEnd && (
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Current Period</p>
                      <p className="font-medium text-sm">
                        {formatDate(subscription.currentPeriodStart)} &ndash;{' '}
                        {formatDate(subscription.currentPeriodEnd)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Cancelled info */}
                {isCancelled && subscription.endDate && (
                  <div className="rounded-lg border border-muted bg-muted/30 p-3 flex items-center gap-3">
                    <XCircle className="h-5 w-5 text-muted-foreground shrink-0" />
                    <div>
                      <p className="text-sm font-medium">Subscription cancelled</p>
                      <p className="text-xs text-muted-foreground">
                        Access ends on {formatDate(subscription.endDate)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Auto-renewal info */}
                {!isCancelled && (
                  <p className="text-xs text-muted-foreground">
                    Auto-renewal is {subscription.autoRenewal ? 'enabled' : 'disabled'}.
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Usage summary card (if applicable) */}
          {subscription.amountPaid !== undefined && subscription.amountPaid > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Payment Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Paid</p>
                      <p className="text-xl font-semibold">
                        {formatCurrency(subscription.amountPaid, subscription.currency)}
                      </p>
                    </div>
                    {subscription.lastPaymentDate && (
                      <div>
                        <p className="text-sm text-muted-foreground">Last Payment</p>
                        <p className="font-medium">{formatDate(subscription.lastPaymentDate)}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Quick actions */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Upgrade CTA for Free plan users */}
                  {isFreePlan && !isCancelled && (
                    <Button
                      className="justify-between h-auto py-3 px-4 bg-gradient-to-r from-purple-600 to-primary text-white sm:col-span-2"
                      onClick={() => navigate('/billing/plans')}
                    >
                      <span className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4" />
                        Upgrade Your Plan
                      </span>
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  )}

                  {!isCancelled && !isPaused && (
                    <Button
                      variant="outline"
                      className="justify-between h-auto py-3 px-4"
                      onClick={() => navigate('/billing/plans')}
                    >
                      <span className="flex items-center gap-2">
                        <RefreshCw className="h-4 w-4" />
                        Change Plan
                      </span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  )}

                  <Button
                    variant="outline"
                    className="justify-between h-auto py-3 px-4"
                    onClick={() => navigate('/billing/history')}
                  >
                    <span className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      View Invoices
                    </span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </Button>

                  {/* Pause Subscription (visible when ACTIVE and not free) */}
                  {isActive && !isFreePlan && (
                    <Button
                      variant="outline"
                      className="justify-between h-auto py-3 px-4 text-amber-600 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950"
                      disabled={isPauseResumeLoading}
                      onClick={() => setShowPauseDialog(true)}
                    >
                      <span className="flex items-center gap-2">
                        <Pause className="h-4 w-4" />
                        Pause Subscription
                      </span>
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  )}

                  {/* Resume Subscription (visible when PAUSED) */}
                  {isPaused && (
                    <Button
                      variant="outline"
                      className="justify-between h-auto py-3 px-4 text-emerald-600 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950"
                      disabled={isPauseResumeLoading}
                      onClick={async () => {
                        try {
                          setIsPauseResumeLoading(true);
                          await billingApi.resumeSubscription(subscription.id);
                          toast({ title: 'Subscription resumed', description: 'Your subscription is now active again.' });
                          fetchSubscription();
                        } catch (err: unknown) {
                          toast({
                            title: 'Resume failed',
                            description: (err as Error)?.message ?? 'Please try again.',
                            variant: 'destructive',
                          });
                        } finally {
                          setIsPauseResumeLoading(false);
                        }
                      }}
                    >
                      <span className="flex items-center gap-2">
                        {isPauseResumeLoading ? <BrandedSpinner /> : <Play className="h-4 w-4" />}
                        Resume Subscription
                      </span>
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  )}

                  {!isCancelled && !isPaused && (
                    <Button
                      variant="outline"
                      className="justify-between h-auto py-3 px-4 text-destructive hover:text-destructive hover:bg-destructive/5"
                      onClick={() => navigate('/billing/cancel')}
                    >
                      <span className="flex items-center gap-2">
                        <XCircle className="h-4 w-4" />
                        Cancel Subscription
                      </span>
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  )}

                  {isCancelled && (
                    <Button
                      variant="outline"
                      className="justify-between h-auto py-3 px-4 text-emerald-600 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950"
                      onClick={async () => {
                        try {
                          await billingApi.reactivateSubscription(subscription.id);
                          toast({ title: 'Subscription reactivated', description: 'Welcome back!' });
                          fetchSubscription();
                        } catch (err: unknown) {
                          toast({
                            title: 'Reactivation failed',
                            description: (err as Error)?.message ?? 'Please try again.',
                            variant: 'destructive',
                          });
                        }
                      }}
                    >
                      <span className="flex items-center gap-2">
                        <RefreshCw className="h-4 w-4" />
                        Reactivate Subscription
                      </span>
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Subscription dates footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25 }}
            className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-muted-foreground pb-4"
          >
            {subscription.startDate && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Started {formatDate(subscription.startDate)}
              </span>
            )}
            {subscription.createdAt && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Created {formatDate(subscription.createdAt)}
              </span>
            )}
          </motion.div>
          {/* Compare Plans expandable section */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="rounded-xl border border-border bg-card p-4"
          >
            <button
              type="button"
              className="flex w-full items-center justify-between text-left"
              onClick={() => setShowComparePlans((prev) => !prev)}
            >
              <span className="text-sm font-semibold text-foreground">Compare Plans</span>
              {showComparePlans ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
            {showComparePlans && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="mt-4"
              >
                <PlanComparisonTable />
              </motion.div>
            )}
          </motion.div>
        </div>
      </main>
      <Footer />

      {/* Pause confirmation dialog */}
      <Dialog open={showPauseDialog} onOpenChange={setShowPauseDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Pause Subscription?</DialogTitle>
            <DialogDescription className="pt-2">
              Your subscription will be paused and you will not be billed during the pause period.
              You can resume at any time to continue where you left off.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowPauseDialog(false)}
              disabled={isPauseResumeLoading}
            >
              Keep Active
            </Button>
            <Button
              variant="default"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              disabled={isPauseResumeLoading}
              onClick={async () => {
                try {
                  setIsPauseResumeLoading(true);
                  await billingApi.pauseSubscription(subscription.id);
                  toast({ title: 'Subscription paused', description: 'You can resume anytime from this page.' });
                  setShowPauseDialog(false);
                  fetchSubscription();
                } catch (err: unknown) {
                  toast({
                    title: 'Pause failed',
                    description: (err as Error)?.message ?? 'Please try again.',
                    variant: 'destructive',
                  });
                } finally {
                  setIsPauseResumeLoading(false);
                }
              }}
            >
              {isPauseResumeLoading ? (
                <>
                  <BrandedSpinner className="mr-1" />
                  Pausing...
                </>
              ) : (
                <>
                  <Pause className="h-4 w-4 mr-1" />
                  Pause Subscription
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillingDashboardPage;
